import { WebsocketService } from '@aionzdev/modules';
import { Injectable, effect, inject } from '@angular/core';
import { CacheService } from './cache.service';
import { TenantContextService } from './tenant-context.service';

/** Mesmo evento/salas que PurchasingService.getRealtimeConfig() usa para as telas realtime. */
const OFERTA_EVENT_NAME = 'oferta-estruturada:atualizada';
const JOIN_EVENT = 'joinOfertaCanal';
const LEAVE_EVENT = 'leaveOfertaCanal';

/** Qualquer `tipo` do evento invalida o mesmo conjunto de prefixos — ver INVALIDATION_MAP do interceptor. */
const OFERTA_INVALIDATED_PREFIXES = ['/api/oferta-estruturada', '/api/pg/negociacao-compra-venda', '/api/canal', '/api/alert'];

/**
 * Emitido pelo backend (PushNotificationService.emitNotification) sempre que
 * uma notificação é criada, cobrindo vários domínios (oferta, canal/trading,
 * pagamento) sob um payload sem campo confiável pra distinguir qual — `type`
 * é sempre 'push' e `url` é um link de frontend, não um path de API. Por
 * isso aqui invalida o cache inteiro, em vez de tentar inferir prefixo.
 */
const NOTIFICATION_EVENT_NAME = 'notification:created';

interface OfertaEstruturadaAtualizadaEvent {
  ofertaId?: number;
  empresaId: number;
  tipo: string;
}

/**
 * Invalida o cache HTTP quando o backend emite eventos de domínio via
 * WebSocket — o cache HTTP por si só não sabe que os dados mudaram por uma
 * ação de outro usuário/aba, só por TTL ou pela própria mutação local (ver
 * http-cache.interceptor.ts).
 */
@Injectable({ providedIn: 'root' })
export class CacheInvalidationRealtimeService {
  private readonly cache = inject(CacheService);
  private readonly tenant = inject(TenantContextService);
  private readonly websocket = inject(WebsocketService);

  private joinedEmpresaId: number | null = null;

  constructor() {
    this.websocket.listen(OFERTA_EVENT_NAME, (event: OfertaEstruturadaAtualizadaEvent) => {
      if (event.empresaId !== this.tenant.companyId()) {
        return;
      }
      OFERTA_INVALIDATED_PREFIXES.forEach(prefix => this.cache.invalidateByPrefix(prefix));
    });

    this.websocket.listen(NOTIFICATION_EVENT_NAME, () => {
      this.cache.clear();
    });

    effect(() => {
      const empresaId = this.tenant.companyId();
      this.rejoinRoom(empresaId);
    });
  }

  private rejoinRoom(empresaId: number | null): void {
    if (this.joinedEmpresaId !== null && this.joinedEmpresaId !== empresaId) {
      this.websocket.sendMessage(LEAVE_EVENT, this.joinedEmpresaId);
    }

    if (empresaId !== null && empresaId !== this.joinedEmpresaId) {
      this.websocket.sendMessage(JOIN_EVENT, empresaId);
    }

    this.joinedEmpresaId = empresaId;
  }
}
